/**
 * Centralized status mapping utility for consistent invoice status handling
 * across the entire application.
 * 
 * This utility ensures that all backend status values are mapped to
 * consistent frontend display values, regardless of case or format.
 */

export type InvoiceStatus = 'paid' | 'unpaid' | 'expired' | 'under_review';

/**
 * Maps backend invoice status to standardized frontend status
 * @param backendStatus - The status value from backend API
 * @returns Standardized frontend status
 */
export function mapInvoiceStatus(backendStatus: string | null | undefined): InvoiceStatus {
  if (!backendStatus) {
    return 'unpaid';
  }

  // Convert to lowercase for case-insensitive comparison
  const statusLower = backendStatus.toLowerCase().trim();

  switch (statusLower) {
    case 'paid':
      return 'paid';
    case 'unpaid':
      return 'unpaid';
    case 'expired':
      return 'expired';
    case 'under review':
    case 'under_review':
    case 'pending': // Handle legacy "pending" status
      return 'under_review';
    default:
      console.warn(`Unknown invoice status: "${backendStatus}" - defaulting to "unpaid"`);
      return 'unpaid';
  }
}

/**
 * Maps backend invoice status to display-friendly format
 * @param backendStatus - The status value from backend API
 * @returns Display-friendly status string
 */
export function mapInvoiceStatusToDisplay(backendStatus: string | null | undefined): string {
  const status = mapInvoiceStatus(backendStatus);
  
  switch (status) {
    case 'paid':
      return 'Paid';
    case 'unpaid':
      return 'Unpaid';
    case 'expired':
      return 'Expired';
    case 'under_review':
      return 'Under Review';
    default:
      return 'Unpaid';
  }
}

/**
 * Maps backend invoice status to display-friendly format (lowercase)
 * @param backendStatus - The status value from backend API
 * @returns Display-friendly status string (lowercase)
 */
export function mapInvoiceStatusToDisplayLower(backendStatus: string | null | undefined): string {
  const status = mapInvoiceStatus(backendStatus);
  
  switch (status) {
    case 'paid':
      return 'paid';
    case 'unpaid':
      return 'unpaid';
    case 'expired':
      return 'expired';
    case 'under_review':
      return 'under review';
    default:
      return 'unpaid';
  }
}
